import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles, GraduationCap, Leaf } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const lines = ["L'Esprit", "Brasserie", "façonné par la jeunesse"];

const badges = [
  { icon: GraduationCap, label: "Brasserie Pédagogique" },
  { icon: Sparkles, label: "Formation d'Excellence" },
  { icon: Leaf, label: "Cuisine de Saison" },
];

export default function Hero({ onReserve, onConcept }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <span
        aria-hidden
        className="text-outline pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-serif text-[18vw] font-black leading-none"
      >
        Saint-François
      </span>

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-10 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pb-32">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-gold"
            data-testid="hero-overline"
          >
            Brasserie Pédagogique — Saint-François
          </motion.p>

          <h1 className="font-serif text-4xl font-bold leading-[1.05] tracking-tight text-stone-900 sm:text-5xl lg:text-7xl" data-testid="hero-title">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-burgundy" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg"
            data-testid="hero-subtitle"
          >
            Une brasserie pédagogique unique où les talents de demain — nos élèves en formation —
            vous régalent chaque semaine, en cuisine comme en salle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onReserve}
              className="group flex items-center gap-2 rounded-full bg-burgundy px-7 py-3.5 text-sm font-semibold text-cream shadow-xl shadow-burgundy/30 transition-all hover:-translate-y-0.5 hover:bg-burgundy-dark"
              data-testid="hero-reserve-button"
            >
              Réserver une table
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={onConcept}
              className="rounded-full border border-stone-300 px-7 py-3.5 text-sm font-semibold text-stone-700 transition-colors hover:border-burgundy hover:text-burgundy"
              data-testid="hero-concept-button"
            >
              Voir la galerie
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {badges.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center gap-2 rounded-full border border-gold/40 bg-gold-light/50 px-4 py-1.5 text-xs font-medium text-stone-700"
              >
                <Icon className="h-3.5 w-3.5 text-gold" />
                {label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -left-4 -top-4 h-full w-full rounded-t-[10rem] border border-gold/50" aria-hidden />
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-t-[10rem] shadow-2xl shadow-burgundy/20">
              <img
                src={HERO_IMG}
                alt="Assiette gastronomique dressée par nos élèves"
                className="max-h-[80vh] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-burgundy-deep/30 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="absolute -bottom-6 -left-4 rounded-2xl border border-gold/30 bg-white/90 px-5 py-4 shadow-xl backdrop-blur-md sm:-left-10"
              data-testid="hero-menu-card"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Menu Unique</p>
              <p className="mt-1 font-serif text-2xl font-bold text-burgundy">
                5 € <span className="font-sans text-sm font-medium text-stone-500">profs &amp; élèves</span>
              </p>
              <p className="text-xs text-stone-500">7 € visiteurs externes</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
